// V103-7-6: starter templates for the plans and documents a GovRAMP Core
// submission requires (configuration management plan, incident response plan,
// contingency plan, rules of behavior, user guide, plans catalog, laws and
// regulations), published through the Policies module (policy-ack-service).
//
// Wording is GAC Pro's own and framework-neutral: no GovRAMP, FedRAMP or NIST
// text is reproduced; related 800-53 controls are listed by id only. Each
// template ends in an attestation block the approving official completes. A
// workspace owner/admin publishing the edited plan IS the approval path
// (POST /api/w/:slug/policies, `published_by_user_id` + body hash). Owner
// review of these skeletons is pending (V103-8-6).
//
// Pure data — no runtime imports.
export const GOVRAMP_PLAN_TEMPLATES_VERSION = 'gacpro-govramp-plans@1';
function plan(s) {
    return [
        `# ${s.title}`, '',
        '## Purpose', '', s.purpose, '',
        '## Scope', '', s.scope, '',
        '## Roles & Responsibilities', '', ...s.roles.map((r) => `- ${r}`), '',
        ...s.sections.flatMap((sec) => [`## ${sec.heading}`, '', ...sec.items.map((i) => `- ${i}`), '']),
        '## Review & Maintenance', '', s.review, '',
        '## Attestation', '',
        'I attest that this document describes how the system is actually operated, that the people named in it have accepted their responsibilities, and that it will be kept current as described above.',
        '',
        '- Approving official: [name, title]',
        '- Date approved: [YYYY-MM-DD]',
        '- Next review due: [YYYY-MM-DD]',
        '',
    ].join('\n');
}
const SYSTEM_SCOPE = 'The system inside the authorization boundary: every component, interconnection and service listed in the system inventory, the people who operate it, and the locations it runs from.';
export const GOVRAMP_PLAN_TEMPLATES = [
    {
        key: 'configuration_management_plan',
        title: 'Configuration Management Plan',
        submission_item: 'plans_catalog',
        related_controls: ['cm-1', 'cm-2', 'cm-3', 'cm-6', 'cm-8', 'cm-9'],
        body_markdown: plan({
            title: 'Configuration Management Plan',
            purpose: 'Describe how the system\'s configuration items are identified, baselined, changed and kept consistent with the approved baseline.',
            scope: SYSTEM_SCOPE,
            roles: [
                'System owner: approves this plan and the configuration baselines.',
                'Change approvers: review and approve changes before deployment.',
                'Engineers: make changes only through the approved process and record them.',
            ],
            sections: [
                { heading: 'Configuration Items', items: ['Configuration items are the components in the system inventory, their infrastructure definitions and their application code.', 'Each item has an owner and a recorded baseline.'] },
                { heading: 'Baselines', items: ['Baselines are defined as code where possible and stored in version control.', 'Deviations from a baseline are detected automatically and recorded as findings.'] },
                { heading: 'Change Control', items: ['Every change is proposed, reviewed by someone other than its author, tested and approved before deployment.', 'Emergency changes are reviewed after the fact within an agreed number of business days.', 'Security impact is assessed for each change; significant changes are reported as required.'] },
                { heading: 'Inventory', items: ['The system inventory is updated when components are added, changed or removed, and reconciled at least monthly.'] },
            ],
            review: 'This plan is reviewed at least annually and after any significant change to the system.',
        }),
    },
    {
        key: 'incident_response_plan',
        title: 'Incident Response Plan',
        submission_item: 'plans_catalog',
        related_controls: ['ir-1', 'ir-4', 'ir-5', 'ir-6', 'ir-8'],
        body_markdown: plan({
            title: 'Incident Response Plan',
            purpose: 'Set out how security incidents affecting the system are detected, reported, contained, eradicated and recovered from, and how affected customers and authorities are notified.',
            scope: SYSTEM_SCOPE,
            roles: [
                'Incident commander: leads the response and decides on escalation.',
                'Responders: investigate, contain and recover under the commander\'s direction.',
                'Communications lead: notifies customers, partners and authorities within the required timeframes.',
            ],
            sections: [
                { heading: 'Detection & Reporting', items: ['Alerts, user reports and third-party notices are triaged within defined service levels.', 'Anyone can report a suspected incident through the published channel.'] },
                { heading: 'Response Phases', items: ['Containment limits impact while preserving evidence.', 'Eradication removes the cause; recovery restores service from known-good state.', 'Each incident gets a timeline, a severity and an owner.'] },
                { heading: 'Notification', items: ['Customers and oversight bodies are notified within the timeframes their agreements and programs require.', 'Notification decisions and times are recorded.'] },
                { heading: 'Lessons Learned', items: ['A post-incident review records root cause and improvements, tracked to completion.'] },
                { heading: 'Testing', items: ['The plan is exercised at least annually; results feed updates to this plan.'] },
            ],
            review: 'This plan is reviewed at least annually, after each significant incident and after each exercise.',
        }),
    },
    {
        key: 'contingency_plan',
        title: 'Contingency Plan',
        submission_item: 'plans_catalog',
        related_controls: ['cp-1', 'cp-2', 'cp-4', 'cp-9', 'cp-10'],
        body_markdown: plan({
            title: 'Contingency Plan',
            purpose: 'Describe how the system keeps essential functions running, and how it is restored, after a disruption.',
            scope: SYSTEM_SCOPE,
            roles: [
                'Contingency plan coordinator: maintains this plan and leads activation.',
                'Recovery team: restores services and data in the defined order.',
                'System owner: declares a disruption and approves return to normal operations.',
            ],
            sections: [
                { heading: 'Recovery Objectives', items: ['Recovery time and recovery point objectives are defined for each essential function.'] },
                { heading: 'Backups', items: ['Data is backed up on a defined schedule, protected, and restorable; restores are tested.'] },
                { heading: 'Activation & Recovery', items: ['Criteria for activation, notification steps and the recovery sequence are documented.', 'Alternate processing relies on the hosting provider\'s redundancy where it is inherited.'] },
                { heading: 'Reconstitution', items: ['The system returns to normal operation only after its integrity is verified.'] },
                { heading: 'Testing', items: ['The plan is tested at least annually; results and corrective actions are recorded.'] },
            ],
            review: 'This plan is reviewed at least annually and after each test or activation.',
        }),
    },
    {
        key: 'rules_of_behavior',
        title: 'Rules of Behavior',
        submission_item: 'rules_of_behavior',
        related_controls: ['pl-4', 'ac-8', 'ps-6'],
        body_markdown: plan({
            title: 'Rules of Behavior',
            purpose: 'State the responsibilities and expected behavior of everyone with access to the system, and the consequences of not following them.',
            scope: 'Every person with privileged or non-privileged access to the system, including employees, contractors and customer administrators.',
            roles: [
                'Users: read, accept and follow these rules before access is granted and when they change.',
                'Managers: make sure their staff have accepted the current rules.',
                'Security lead: maintains these rules and handles reported violations.',
            ],
            sections: [
                { heading: 'Rules', items: [
                        'Use only your own account; never share credentials or multi-factor devices.',
                        'Access only the data and functions you need for your role.',
                        'Protect information according to its classification; never move it outside approved systems.',
                        'Report suspected incidents, lost devices and phishing immediately.',
                        'Do not attempt to bypass security controls or test them without authorization.',
                    ] },
                { heading: 'Acknowledgment', items: ['Acceptance is recorded before access is granted and again after material changes.'] },
                { heading: 'Consequences', items: ['Violations can lead to loss of access and disciplinary or contractual action.'] },
            ],
            review: 'These rules are reviewed at least annually; users re-accept material changes.',
        }),
    },
    {
        key: 'user_guide',
        title: 'User Guide',
        submission_item: 'user_guide',
        related_controls: ['sa-5'],
        body_markdown: plan({
            title: 'User Guide',
            purpose: 'Explain to customers how to configure and use the service securely, and which security responsibilities stay with them.',
            scope: 'Customer administrators and end users of the service.',
            roles: [
                'Product owner: keeps this guide accurate for each release.',
                'Security lead: reviews the secure-configuration and responsibility sections.',
            ],
            sections: [
                { heading: 'Getting Started', items: ['How to sign in, set up single sign-on and multi-factor authentication, and invite users.'] },
                { heading: 'Secure Configuration', items: ['Recommended settings, roles and least-privilege access for administrators.', 'How to review audit logs and export data.'] },
                { heading: 'Customer Responsibilities', items: ['The controls the customer implements, as listed in the customer responsibility matrix.'] },
                { heading: 'Support & Reporting', items: ['How to get help and how to report a security concern.'] },
            ],
            review: 'This guide is reviewed with every significant release and at least annually.',
        }),
    },
    {
        key: 'plans_catalog',
        title: 'Plans Catalog',
        submission_item: 'plans_catalog',
        related_controls: ['pl-2', 'pm-9'],
        body_markdown: plan({
            title: 'Plans Catalog',
            purpose: 'List every plan that supports the system\'s security program, with its owner, approval date and next review.',
            scope: SYSTEM_SCOPE,
            roles: ['Security lead: keeps this catalog current.', 'Plan owners: keep their plans reviewed on schedule.'],
            sections: [
                { heading: 'Plans', items: [
                        'Configuration Management Plan — owner, approved, next review.',
                        'Incident Response Plan — owner, approved, next review.',
                        'Contingency Plan — owner, approved, next review.',
                        'Continuous Monitoring Strategy — owner, approved, next review.',
                        'Any other plan the system relies on (e.g. supply chain, privacy) — owner, approved, next review.',
                    ] },
            ],
            review: 'This catalog is reviewed whenever a plan is added, retired or re-approved, and at least annually.',
        }),
    },
    {
        key: 'laws_and_regulations',
        title: 'Applicable Laws and Regulations',
        submission_item: 'laws_regulations',
        related_controls: ['pl-2', 'pm-28'],
        body_markdown: plan({
            title: 'Applicable Laws and Regulations',
            purpose: 'Identify the laws, regulations, standards and contractual requirements that apply to the system and the information it processes.',
            scope: SYSTEM_SCOPE,
            roles: ['Legal / compliance lead: maintains this list and tracks changes.', 'System owner: confirms the list reflects the information the system handles.'],
            sections: [
                { heading: 'Requirements', items: [
                        'Each entry: the law, regulation or standard; the jurisdiction or customer it comes from; the information types it covers; and how the system meets it.',
                        'State and local data-protection and breach-notification laws for the jurisdictions served.',
                        'Sector rules for the information types processed (for example health, education, criminal justice or tax data).',
                    ] },
            ],
            review: 'This list is reviewed at least annually and when the system starts serving a new jurisdiction or information type.',
        }),
    },
];
export function getGovrampPlanTemplate(key) {
    return GOVRAMP_PLAN_TEMPLATES.find((t) => t.key === key);
}
//# sourceMappingURL=govramp-plan-templates.js.map